/**
 * Servicio para la gestión de contratos
 * Fecha: 16 Oct 2025
 */

import { apiRequest, API_URL, getAuthToken } from '@/shared/lib/apiUtils';
import type {
  Contract,
  ContractFilters,
  GenerateContractDto,
} from '@/types/employee.types';

/**
 * Servicio de contratos con todos los endpoints disponibles
 */
export const contractsService = {
  /**
   * Listar contratos con filtros opcionales
   */
  getAll: async (filters?: ContractFilters): Promise<Contract[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const queryString = params.toString();
    const url = queryString
      ? `${API_URL}/contracts?${queryString}`
      : `${API_URL}/contracts`;
    return apiRequest<Contract[]>(url);
  },

  /**
   * Obtener contrato por ID
   */
  getById: async (id: number): Promise<Contract> => {
    return apiRequest<Contract>(`${API_URL}/contracts/${id}`);
  },

  /**
   * Obtener contrato activo de un empleado
   */
  getActiveByEmployee: async (employeeId: number): Promise<Contract> => {
    return apiRequest<Contract>(`${API_URL}/contracts/employee/${employeeId}/active`);
  },

  /**
   * Obtener todos los contratos de un empleado
   */
  getByEmployee: async (employeeId: number): Promise<{ data: Contract[]; total: number }> => {
    return apiRequest<{ data: Contract[]; total: number }>(`${API_URL}/employees/${employeeId}/contracts`);
  },

  /**
   * Crear contrato manualmente
   */
  create: async (data: Partial<Contract>): Promise<Contract> => {
    return apiRequest<Contract>(`${API_URL}/contracts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Generar contrato desde plantilla con variables automáticas
   */
  generate: async (data: GenerateContractDto): Promise<Contract> => {
    return apiRequest<Contract>(`${API_URL}/contracts/generate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Subir contrato firmado (PDF)
   */
  uploadSigned: async (id: number, file: File): Promise<Contract> => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/contracts/${id}/upload-signed`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw {
        message: errorData.message || 'Error al subir contrato firmado',
        error: errorData.error || 'Error desconocido',
        statusCode: response.status,
      };
    }

    return await response.json();
  },

  /**
   * Activar contrato
   */
  activate: async (id: number): Promise<Contract> => {
    return apiRequest<Contract>(`${API_URL}/contracts/${id}/activate`, {
      method: 'POST',
    });
  },

  /**
   * Terminar contrato
   */
  terminate: async (id: number): Promise<Contract> => {
    return apiRequest<Contract>(`${API_URL}/contracts/${id}/terminate`, {
      method: 'POST',
    });
  },

  /**
   * Actualizar contrato
   */
  update: async (id: number, data: Partial<Contract>): Promise<Contract> => {
    return apiRequest<Contract>(`${API_URL}/contracts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Subir contrato (Word) a un empleado
   */
  uploadContract: async (employeeId: number, file: File, formData: {
     category: string,
     state: string,
     version: string,
     expirationDate: string,
  }): Promise<Contract> => {
    const token = getAuthToken();
    const formDataSend = new FormData();
    formDataSend.append('file', file);
    formDataSend.append('nombre', file.name);
    formDataSend.append('categoria', formData.category);
    formDataSend.append('estado', formData.state);
    formDataSend.append('version', formData.version);
    formDataSend.append('vencimiento', formData.expirationDate);

    const response = await fetch(`${API_URL}/employees/${employeeId}/contracts/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formDataSend,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw {
        message: errorData.message || 'Error al subir contrato',
        error: errorData.error || 'Error desconocido',
        statusCode: response.status,
      };
    }

    return await response.json();
  },
};
